import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getQuestionData } from "@icarius-pages/myTraining/selectors";
import { getLocalizedString } from "@icarius-localization/strings";
import { Button, Chip, FormControl, IconButton, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import FavoriteIcon from "@material-ui/icons/Favorite";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import Container from "@icarius-pages/myTraining/components/container";
import { getFullDateDayName } from "@icarius-utils/date";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { getAppColor, getUserData } from "@icarius-app/selectors";
import DotsMenu from "@icarius-common/dotsMenu";
import {
    closeQuestionFromTrainingAction,
    deleteQuestionFromTrainingAction,
    getActivityFromSpecificTrainingAction,
    getQuestionAndAnswersFromTrainingAction,
    voteQuestionFromTrainingAction,
} from "@icarius-pages/myTraining/actions";
import QuestionAndAnswersDialog from "./questionAndAnswersDialog";
import CreateQuestionDialog from "./createQuestionDialog";

const QAndATab = (props) => {
    const { trainingData, activityData } = props;

    const [filter, setFilter] = useState("");
    const [questionAndAnswersDialogIsOpen, setQuestionAndAnswersDialogIsOpen] = useState(false);
    const [createQuestionDialogIsOpen, setCreateQuestionDialogIsOpen] = useState(false);

    const dispatch = useDispatch();
    const questionData = useSelector(getQuestionData);
    const color = useSelector(getAppColor);
    const userData = useSelector(getUserData);

    const handleOpenQuestionAndAnswersDialog = (questionId) => {
        dispatch(getQuestionAndAnswersFromTrainingAction(trainingData.id, activityData.id, questionId)).then((resp) => {
            if (resp.status === 200) {
                setQuestionAndAnswersDialogIsOpen(true);
            }
        });
    };

    const handleCloseQuestionAndAnswerDialog = () => {
        setQuestionAndAnswersDialogIsOpen(false);
        dispatch(getActivityFromSpecificTrainingAction(trainingData.id, activityData.id));
    };

    const handleVoteQuestion = (questionId) => {
        dispatch(voteQuestionFromTrainingAction(trainingData.id, activityData.id, questionId));
    };

    const handleCloseQuestion = (questionId) => {
        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("closeQuestionConfirmation"),
                onConfirm: () =>
                    dispatch(closeQuestionFromTrainingAction(trainingData.id, activityData.id, questionId)),
            })
        );
    };

    const handleDeleteQuestion = (questionId) => {
        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("deleteQuestionConfirmation"),
                onConfirm: () => {
                    dispatch(deleteQuestionFromTrainingAction(trainingData.id, activityData.id, questionId));
                },
            })
        );
    };

    const filteredQuestions = useMemo(() => {
        if (!activityData.questions?.length) return [];
        if (!filter) return activityData.questions;
        return activityData.questions.filter((question) => {
            if (filter === "closed" && question.isClosed) return true;
            if (filter === "notClosed" && !question.isClosed) return true;
            return false;
        });
    }, [filter, activityData.questions]);

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                }}
            >
                <FormControl style={{ width: "100%", marginBottom: 5 }}>
                    <InputLabel shrink id={"filterLabel"}>
                        {getLocalizedString("filter")}
                    </InputLabel>
                    <Select
                        displayEmpty
                        value={filter}
                        labelId={"filterLabel"}
                        id={"filter"}
                        onChange={(e) => setFilter(e.target.value)}
                        margin={"none"}
                    >
                        {[
                            { value: getLocalizedString("all"), key: "" },
                            { value: getLocalizedString("openQuestions"), key: "notClosed" },
                            { value: getLocalizedString("closedQuestions"), key: "closed" },
                        ].map((item) => (
                            <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                {item.value}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {!trainingData.isFinished && trainingData.isParticipant && (
                    <Button
                        fullWidth
                        variant="contained"
                        className="whiteText"
                        style={{ background: color, border: 0 }}
                        onClick={() => setCreateQuestionDialogIsOpen(true)}
                    >
                        {getLocalizedString("ask")}
                    </Button>
                )}
                {!filteredQuestions?.length && (
                    <div style={{ width: "100%" }}>
                        <Alert variant="filled" severity="info">
                            {getLocalizedString("noQuestionsMatchFilterQAndA")}
                        </Alert>
                    </div>
                )}
                {filteredQuestions.map((question) => {
                    const isOwner = userData.code === question.owner.code;

                    return (
                        <div key={question.id} style={{ width: "100%" }}>
                            <Container
                                style={{
                                    width: "100%",
                                    color: "var(--mainText)",
                                }}
                            >
                                <div
                                    onClick={() => handleOpenQuestionAndAnswersDialog(question.id)}
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 5,
                                        cursor: "pointer",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            columnGap: 10,
                                        }}
                                    >
                                        <Typography style={{ fontSize: 18, fontWeight: 600 }}>
                                            {question.title}
                                        </Typography>
                                        {question.isClosed && (
                                            <Chip
                                                size="small"
                                                label={getLocalizedString("closed")}
                                                style={{
                                                    color: "white",
                                                    background: "var(--ownBlue)",
                                                }}
                                            />
                                        )}
                                        {question.isPending && (
                                            <Chip
                                                size="small"
                                                label={getLocalizedString("pending")}
                                                style={{
                                                    background: "var(--ownRed)",
                                                    color: "white",
                                                }}
                                            />
                                        )}
                                    </div>
                                    <Typography style={{ fontSize: 14 }}>
                                        {getLocalizedString("byAlt")} {question.owner.name}
                                    </Typography>
                                    <Typography variant="caption">
                                        {getFullDateDayName(question.date)} - {question.time}{" "}
                                        {getLocalizedString("hrs")}
                                    </Typography>
                                    {(trainingData.isInstructor || isOwner) && !question.isClosed && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                right: -5,
                                            }}
                                        >
                                            <DotsMenu noPadding>
                                                {[
                                                    <MenuItem key={1} onClick={() => handleCloseQuestion(question.id)}>
                                                        {getLocalizedString("markAsSolved")}
                                                    </MenuItem>,
                                                    isOwner && question.canEdit && (
                                                        <MenuItem
                                                            key={3}
                                                            onClick={() => handleDeleteQuestion(question.id)}
                                                        >
                                                            {getLocalizedString("delete")}
                                                        </MenuItem>
                                                    ),
                                                ]}
                                            </DotsMenu>
                                        </div>
                                    )}
                                </div>
                                <div
                                    style={{
                                        marginTop: 10,
                                        display: "flex",
                                        gap: 15,
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <div
                                        onClick={() => handleOpenQuestionAndAnswersDialog(question.id)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                fontSize: "inherit",
                                                cursor: "pointer",
                                            }}
                                        >
                                            {question.answers}
                                        </Typography>
                                        <IconButton style={{ padding: 0 }}>
                                            <ChatBubbleOutlineIcon style={{ height: 25, color: "var(--mainText)" }} />
                                        </IconButton>
                                    </div>
                                    <div
                                        onClick={() => {
                                            if (trainingData.isFinished) return;
                                            handleVoteQuestion(question.id);
                                        }}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                fontSize: "inherit",
                                                cursor: "pointer",
                                            }}
                                        >
                                            {question.votes}
                                        </Typography>
                                        <IconButton style={{ padding: 0 }}>
                                            {question.liked ? (
                                                <FavoriteIcon style={{ height: 25, color: "var(--mainText)" }} />
                                            ) : (
                                                <FavoriteBorderIcon style={{ height: 25, color: "var(--mainText)" }} />
                                            )}
                                        </IconButton>
                                    </div>
                                </div>
                            </Container>
                        </div>
                    );
                })}
            </div>
            {questionAndAnswersDialogIsOpen && (
                <QuestionAndAnswersDialog
                    open={questionAndAnswersDialogIsOpen}
                    trainingData={trainingData}
                    activityData={activityData}
                    questionData={questionData}
                    handleClose={handleCloseQuestionAndAnswerDialog}
                />
            )}
            {createQuestionDialogIsOpen && (
                <CreateQuestionDialog
                    open={createQuestionDialogIsOpen}
                    trainingData={trainingData}
                    activityData={activityData}
                    handleClose={() => setCreateQuestionDialogIsOpen(false)}
                />
            )}
        </>
    );
};

export default QAndATab;
